import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { productsAPI } from "../../services/productsService";
import { IFilters, IProduct } from "Interfaces/interface.productsapi";
import { RootState } from "store/Store";

const productsAdapter = createEntityAdapter({
  selectId: (product: IProduct) => product.id || "",
});

interface ProductsState {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  totalCount: number;
  currentPage: number;
  hasReachedEnd: boolean;
  selectedProduct: IProduct | null;
  isProductLoaded: boolean;
  similarProducts: IProduct[];
  similarProductsStatus: "idle" | "loading" | "succeeded" | "failed";
}

const initialState = productsAdapter.getInitialState<ProductsState>({
  status: "idle",
  error: null,
  totalCount: 0,
  currentPage: 1,
  hasReachedEnd: false,
  selectedProduct: null,
  isProductLoaded: false,
  similarProducts: [],
  similarProductsStatus: "idle",
});

export const fetchProducts = createAsyncThunk(
  "products/fetchProducts",
  async (filters: IFilters, { rejectWithValue }) => {
    try {
      const response = await productsAPI.fetchProducts(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProductById = createAsyncThunk(
  "products/fetchProductById",
  async (id: string, { rejectWithValue }) => {
    try {
      const product = await productsAPI.fetchProductById(id);
      return product;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProductsByKeyword = createAsyncThunk(
  "products/fetchProductsByKeyword",
  async (keywords: string[], { rejectWithValue }) => {
    try {
      const products = await productsAPI.fetchProductsByKeyword(keywords);
      return products;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);


export const fetchProductsBySubCategory = createAsyncThunk(
  "products/fetchProductsBySubCategory",
  async (subCategoryIds: string[], { rejectWithValue }) => {
    try {
      const products = await productsAPI.fetchProductsBySubCategory(subCategoryIds);
      return products;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    resetProducts: (state) => {
      productsAdapter.removeAll(state);
      state.totalCount = 0;
      state.hasReachedEnd = false;
      state.isProductLoaded = false;
    },
    removeProduct: (state, action) => {
      const productId = action.payload;
      const productRemoved = productsAdapter.removeOne(state, productId);
      if (productRemoved) {
        state.totalCount = Math.max(0, state.totalCount - 1);
      }
    },
    updateProduct: (state, action) => {
      const updatedProduct = action.payload;
      productsAdapter.upsertOne(state, updatedProduct);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        productsAdapter.upsertMany(state, action.payload.data);
        state.totalCount = action.payload.total;
        const fetchedPage = action.meta.arg.page || 1;
        const pageSize = action.meta.arg.pageSize || 20;
        state.hasReachedEnd = fetchedPage * pageSize >= state.totalCount;
        state.isProductLoaded = true;
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(fetchProductById.pending, (state) => {
        state.status = "loading";
        state.isProductLoaded = false;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.selectedProduct = action.payload;
        state.isProductLoaded = false;
        state.status = "succeeded";
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(fetchProductsByKeyword.pending, (state) => {
        state.similarProductsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchProductsByKeyword.fulfilled, (state, action) => {
        state.similarProducts = action.payload;
        state.similarProductsStatus = "succeeded";
      })
      .addCase(fetchProductsByKeyword.rejected, (state, action) => {
        state.similarProductsStatus = "failed";
        state.error = action.payload as string;
      })
      .addCase(fetchProductsBySubCategory.pending, (state) => {
        state.similarProductsStatus = "loading";
        state.error = null;
      })
      .addCase(fetchProductsBySubCategory.fulfilled, (state, action) => {
        state.similarProducts = action.payload;
        state.similarProductsStatus = "succeeded";
      })
      .addCase(fetchProductsBySubCategory.rejected, (state, action) => {
        state.similarProductsStatus = "failed";
        state.error = action.payload as string;
      });
  },
});

export const {
  clearSelectedProduct,
  setCurrentPage,
  resetProducts,
  removeProduct,
  updateProduct,
} = productsSlice.actions;
export default productsSlice.reducer;

const selectProductsState = (state: RootState) => state.products;

export const {
  selectAll: selectAllProducts,
  selectById: selectProductById,
  selectEntities: selectProductEntities,
  selectTotal: selectTotalProducts,
} = productsAdapter.getSelectors(selectProductsState);

export const selectProductStatus = (state: RootState) => state.products.status;
export const selectProductError = (state: RootState) => state.products.error;
export const selectProductTotalCount = (state: RootState) => state.products.totalCount;
export const selectCurrentPage = (state: RootState) => state.products.currentPage;
export const selectHasReachedEnd = (state: RootState) => state.products.hasReachedEnd;
export const selectSelectedProduct = (state: RootState) => state.products.selectedProduct;
export const selectIsProductLoaded = (state: RootState) => state.products.isProductLoaded;
export const selectSimilarProducts = (state: RootState) => state.products.similarProducts;
export const selectSimilarProductsStatus = (state: RootState) => state.products.similarProductsStatus;
